import React, { useEffect, useState } from 'react';
import { cachedHistory, requestHistory } from './paddockHistoryClient';
import './PaddockHistoryCard.css';

const FLAGS = { hari: 'ハリ', gaiboki: '外歩き', hakkan: '発汗', yurui: 'ゆるい', twoman: '二人引', saigo: '最後尾', kaesiOK: '返し馬○', kaesiNG: '返し馬×' };
const countText = values => Object.entries(values || {}).map(([name, count]) => `${name} ${count}人`).join(' ／ ');
function observations(paddock) {
  const counts = paddock?.counts || {};
  const selections = ['attitude', 'body', 'walk'].flatMap(field => Object.entries(counts[field] || {}).filter(([key, n]) => n > 0 && !['未選択', 'その他'].includes(key)).map(([key, n]) => `${key} ${n}/${paddock.input_users}人`));
  return selections.concat(Object.entries(FLAGS).filter(([key]) => counts.flags?.[key] > 0).map(([key, label]) => `${label} ${counts.flags[key]}/${paddock.input_users}人`));
}
function DiagnosisCounts({ paddock }) {
  if (!paddock?.saved_users) return <p className="ph-caption">診断データなし</p>;
  if (!paddock.input_users) return <p className="ph-caption">保存あり・診断項目とコメントは未入力</p>;
  const counts = paddock.counts || {}, selected = observations(paddock);
  const coreSelected = ['body', 'attitude', 'walk'].some(f => Object.entries(counts[f] || {}).some(([key, n]) => n > 0 && key !== '未選択'));
  return <>
    <p className="ph-observation">診断 {paddock.input_users}人：{selected.length ? selected.join(' ／ ') : 'コメントのみ'}</p>
    <p className="ph-caption">保存評価：{countText(counts.evaluation)}{!coreSelected ? ' ／ 体型・気配・歩様は未選択' : ''}</p>
    <details><summary>全項目の人数</summary><dl className="ph-counts">
      {[['body', '体型'], ['attitude', '気配'], ['walk', '歩様']].map(([key, label]) => <React.Fragment key={key}><dt>{label}</dt><dd>{countText(counts[key])}</dd></React.Fragment>)}
      <dt>その他</dt><dd>{Object.entries(FLAGS).map(([key, label]) => `${label} ${counts.flags?.[key] || 0}人`).join(' ／ ')}</dd>
    </dl></details>
  </>;
}
function HistoryDetail({ raceId, horse, revision }) {
  const [pages, setPages] = useState([]), [page, setPage] = useState(1), [status, setStatus] = useState('loading');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true, operation;
    const params = { race_id: raceId, mode: 'detail', horse_no: horse.horse_no, revision, page: String(page) };
    const accept = data => {
      if (data.ketto_num !== horse.ketto_num) throw new Error('馬の血統登録番号が一致しません');
      if (current) { setPages(prev => [...prev.filter(p => p.page !== data.page), data].sort((a, b) => a.page - b.page)); setStatus('ready'); }
    };
    setStatus('loading');
    const cached = cachedHistory(params);
    if (cached) { try { accept(cached); } catch { setStatus('error'); } }
    else {
      operation = requestHistory(params);
      operation.response.then(accept).catch(() => { if (current) setStatus('error'); });
    }
    return () => { current = false; operation?.cancel(); };
  }, [raceId, horse.horse_no, horse.ketto_num, revision, page, reload]);
  const runs = pages.flatMap(p => p.runs), hasMore = pages[pages.length - 1]?.has_more;
  return <div className="ph-runs" aria-live="polite">
    {runs.map(run => <article className="ph-run" key={`${run.race_id}-${run.horse_no}`}>
      <div className="ph-run-title">{run.date}　{run.track}{run.race_no}R　{run.rank ? `${run.rank}着` : run.result_status}</div>
      <div>{run.race_name}{run.surface}{run.distance ? `${run.distance}m` : ''} {run.baba}</div>
      <div>{run.popularity ? `${run.popularity}人気` : '人気未取得'} ／ {run.time || 'タイム未取得'} ／ {run.jockey || '騎手未取得'}</div>
      <div className="ph-caption">通過順：{run.corners.map(n => n || '—').join(' → ')}</div>
      <DiagnosisCounts paddock={run.paddock} />
    </article>)}
    {status === 'loading' && <p className="ph-caption" role="status">過去走を読み込み中…</p>}
    {status === 'error' && <p className="ph-caption">過去走を取得できませんでした。<button type="button" onClick={() => setReload(n => n + 1)}>再取得</button></p>}
    {status === 'ready' && hasMore && <button type="button" className="ph-more" onClick={() => setPage(n => n + 1)}>さらに5走を見る</button>}
  </div>;
}
function PaddockHistoryCard({ raceId, horseNo, horseName, kettoNum, state }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [raceId, horseNo, state.data?.revision]);
  const horse = state.data?.horses.find(h => Number(h.horse_no) === Number(horseNo));
  const matches = horse && (kettoNum ? String(kettoNum).trim() === horse.ketto_num : String(horseName || '').replace(/\s/g, '') === horse.name.replace(/\s/g, ''));
  const latest = matches ? horse.latest : null, paddock = latest?.paddock;
  const selected = observations(paddock);
  return <section className="ph-history-card" aria-label={`${horseName}の過去診断`}>
    <div className="ph-summary-slot">
      {state.status === 'ready' && matches ? <>
        <div className="ph-summary-title">過去診断：{horse.diagnosed_runs}走・延べ{horse.observations}人</div>
        <div className="ph-caption">{latest ? `前走 ${latest.date}：${!paddock.saved_users ? '診断データなし' : !paddock.input_users ? '保存のみ・未入力' : selected.length ? selected.slice(0, 3).join(' ／ ') : 'コメントのみ'}` : '過去走なし'}</div>
      </> : <div className="ph-caption">{state.status === 'missing' ? '過去診断データは準備中です' : state.status === 'error' ? '過去診断を取得できませんでした' : state.status === 'ready' ? '出走情報が更新されています。過去診断は再生成待ちです' : '過去診断サマリーを読み込み中…'}{state.status === 'error' && <button type="button" onClick={state.retry}>再取得</button>}</div>}
    </div>
    {matches && horse.run_count > 0 && <details open={open} onToggle={e => setOpen(e.currentTarget.open)}>
      <summary>過去走・診断を比較する（{horse.run_count}走）</summary>
      {open && <><p className="ph-caption">未選択は良否の判定なし。保存評価と観察内容を分けて確認してください。</p><HistoryDetail key={`${raceId}-${horseNo}-${state.data.revision}`} raceId={raceId} horse={horse} revision={state.data.revision} /></>}
    </details>}
  </section>;
}
export default React.memo(PaddockHistoryCard);
